import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BellOff, CheckCheck, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/finance";
import { notifyAlertsChanged } from "@/lib/useAlertCount";

const TARGET = { opportunity: (id) => `/conecta/negocios/${id}`, loan: (id) => `/prestamos/${id}`, investment: (id) => `/inversiones/${id}`, capture: () => "/conecta/revisar" };

export default function AlertasPage() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/alerts", { params: { include_read: true } }).then((r) => { setData(r.data); notifyAlertsChanged(); }).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { api.post("/alerts/run").finally(load); }, [load]);

  const open = async (a) => {
    if (!a.read) await api.post(`/alerts/${a.id}/read`).catch(() => {});
    notifyAlertsChanged();
    const to = TARGET[a.entity_type];
    if (to) nav(to(a.entity_id)); else load();
  };
  const readAll = async () => { await api.post("/alerts/read-all"); toast.success("Todas leídas"); load(); };
  const refresh = async () => { const { data: r } = await api.post("/alerts/run"); toast.success(r.new ? `${r.new} alerta(s) nueva(s)` : "Sin alertas nuevas"); load(); };

  if (!data) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin c-muted" /></div>;
  return (
    <div data-testid="alertas-page" className="space-y-3">
      <div className="flex gap-2">
        <button data-testid="alerts-refresh-btn" onClick={refresh} className="flex-1 c-card py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5"><RefreshCw className="w-4 h-4" /> Actualizar</button>
        <button data-testid="alerts-read-all-btn" disabled={!data.unread} onClick={readAll} className="flex-1 c-card py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50"><CheckCheck className="w-4 h-4" /> Marcar leídas</button>
      </div>
      <p data-testid="alerts-unread-count" className="text-xs c-muted">{data.unread} sin leer</p>
      {data.items.length === 0 && (
        <div data-testid="alerts-empty" className="c-card p-8 flex flex-col items-center gap-2 text-center"><BellOff className="w-8 h-8 c-muted" /><p className="text-sm c-text-2">Sin alertas. Todo al día.</p></div>
      )}
      {data.items.map((a) => (
        <button key={a.id} data-testid="alert-item" onClick={() => open(a)}
          className={`w-full text-left c-card p-4 space-y-1 active:scale-[0.99] transition-transform ${a.read ? "opacity-60" : "!border-l-4 !border-l-[#bc9538]"}`}>
          <p className="font-mono text-[10px] tracking-[0.15em] uppercase c-accent">{a.kind_label}{a.due_date ? ` · ${fmtDate(a.due_date)}` : ""}</p>
          <p data-testid="alert-item-title" className="text-sm font-semibold">{a.title}</p>
          {a.body && <p className="text-xs c-text-2">{a.body}</p>}
        </button>
      ))}
    </div>
  );
}
